import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, resolveImage } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PdfViewerModal from "../components/PdfViewerModal";
import { MapPin, Clock, Loader2, Building2, ArrowLeft, MessageCircle, ChevronRight, FileText, Eye } from "lucide-react";
import { BUSINESS_WHATSAPP, buildWhatsAppLink, formatInquiry } from "../config/contact";

const STATUS_META = {
  upcoming:        { label: "Upcoming",         cls: "bg-amber-50 text-amber-800 border-amber-200" },
  under_planning:  { label: "Under Planning",   cls: "bg-sky-50 text-sky-800 border-sky-200" },
  approved:        { label: "Approved",         cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  in_development:  { label: "In Development",   cls: "bg-primary/10 text-primary border-primary/20" },
};

export default function MasterPlanDetail() {
  const { id } = useParams();
  const [mp, setMp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pdfViewer, setPdfViewer] = useState({ open: false, url: "", title: "" });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/master-plans/${id}`)
      .then((r) => { if (!cancelled) setMp(r.data); })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  function inquire() {
    if (!mp) return;
    const msg = formatInquiry({
      type: "Master Plan Enquiry",
      name: "",
      phone: "",
      location: mp.location,
      details: `I'm interested in the master plan: ${mp.title}. Please share more details.`,
      extra: {
        "Master Plan": mp.title,
        "Status": (STATUS_META[mp.status] || STATUS_META.upcoming).label,
        "Timeline": mp.timeline || "—",
        "Developer": mp.developer_name || "—",
      },
    });
    window.open(buildWhatsAppLink(msg, BUSINESS_WHATSAPP), "_blank", "noopener,noreferrer");
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !mp) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center py-16">
            <Building2 className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <p className="mt-3 font-display text-xl font-semibold">Master plan not found</p>
            <p className="text-sm text-muted-foreground">It may have been unpublished or removed.</p>
            <Link to="/master-plan" className="inline-flex items-center gap-1.5 mt-5 h-10 px-4 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary/90">Back to Master Plans</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const meta = STATUS_META[mp.status] || STATUS_META.upcoming;

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="master-plan-detail">
      <Header />
      <main className="flex-1">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          <Link to="/master-plan" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-ink"><ArrowLeft className="h-4 w-4" /> All Master Plans</Link>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[1.5fr_1fr] gap-6">
          <div>
            <div className="rounded-2xl overflow-hidden border border-border bg-white">
              <img src={mp.image_url} alt={mp.title} className="w-full max-h-[520px] object-cover" />
            </div>
          </div>
          <div>
            <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${meta.cls}`}>{meta.label}</span>
            <h1 className="font-display text-3xl md:text-4xl font-bold mt-3 leading-tight">{mp.title}</h1>
            <div className="mt-3 space-y-1.5 text-sm">
              <p className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> <span>{mp.location}</span></p>
              {mp.timeline && <p className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> <span>Timeline: {mp.timeline}</span></p>}
              {mp.developer_name && <p className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> <span>Developer: {mp.developer_name}</span></p>}
            </div>

            <div className="mt-6 rounded-xl border border-border bg-surface/40 p-4">
              <p className="text-sm font-semibold text-ink">Interested in this plan?</p>
              <p className="text-xs text-muted-foreground mt-1">Send an enquiry to our business team — we&apos;ll share updates, plot availability and next steps.</p>
              <button onClick={inquire} className="mt-3 w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] hover:brightness-95 text-white font-semibold text-sm" data-testid="mp-enquire-btn">
                <MessageCircle className="h-4 w-4" /> Enquire on WhatsApp
              </button>
            </div>

            {mp.pdfs && mp.pdfs.length > 0 && (
              <div className="mt-4 rounded-xl border border-border bg-white p-4" data-testid="mp-pdfs">
                <p className="text-sm font-semibold text-ink mb-2 inline-flex items-center gap-1.5"><FileText className="h-4 w-4 text-primary" /> Documents</p>
                <div className="flex flex-wrap gap-2">
                  {mp.pdfs.map((raw, i) => {
                    const url = resolveImage(raw) || raw;
                    const nice = decodeURIComponent((raw || "").split("/").pop() || `Document ${i + 1}`).slice(-38);
                    return (
                      <button
                        key={`${raw}-${i}`}
                        type="button"
                        onClick={() => setPdfViewer({ open: true, url, title: nice })}
                        className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-semibold px-3 py-1.5 hover:bg-primary/10 transition-colors"
                        data-testid={`mp-pdf-chip-${i}`}
                      >
                        <FileText className="h-3.5 w-3.5" /> <span className="truncate max-w-[160px]">{nice}</span> <Eye className="h-3.5 w-3.5" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-14">
          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
            <div>
              <h2 className="font-display text-2xl font-bold text-ink mb-3">Overview</h2>
              <p className="text-sm text-ink/85 whitespace-pre-line leading-relaxed">{mp.description}</p>
            </div>
            {mp.highlights && mp.highlights.length > 0 && (
              <aside>
                <h2 className="font-display text-2xl font-bold text-ink mb-3">Highlights</h2>
                <ul className="space-y-2">
                  {mp.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <ChevronRight className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <PdfViewerModal
        open={pdfViewer.open}
        pdfUrl={pdfViewer.url}
        title={pdfViewer.title || `${mp.title} — PDF`}
        onClose={() => setPdfViewer({ open: false, url: "", title: "" })}
      />
    </div>
  );
}
